import { apiClient } from './client';
import type { SavedTheme } from '../lib/theme-presets';

/**
 * Users API 클라이언트 — backend `apps/chat-server/src/modules/users/users.routes.ts` (P1-10).
 *
 * 본 사이클은 search 만 — Mail ComposeModal 의 RecipientPicker 가 활용.
 * 향후 user 프로필 조회 / 업데이트는 별도 모듈로 확장.
 *
 * yaml Stage 3 정합은 후속.
 */

export interface UserSummary {
  id: string;
  email: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
  role: string;
  status: string;
}

/**
 * settings-module-v1 — 본인 프로필·비번·환경설정.
 */

export interface ProfileDto extends UserSummary {
  phone: string | null;
  /* 아래는 서버가 이미 내려주던 값(형이 없어 화면이 못 쓰던 것) + 2026-08-20 에 덧붙인 값.
     구 서버에서도 화면이 깨지지 않도록 전부 선택 필드로 둔다. */
  isActive?: boolean;
  lastLoginAt?: string | null;
  createdAt?: string;
  employeeNumber?: string | null;
  institutionName?: string | null;
  /* 입사일 · 고용형태 (2026-08-27) — «내 인사기록카드»(시안 B2)가 읽는다.
     담당자만 고치는 칸이라 화면은 **읽기만** 한다(PatchProfileInput 에 없다).
     옛 서버에서도 화면이 안 깨지도록 선택 필드다. */
  hireDate?: string | null;
  employmentType?: string | null;
  /* 프로필 새 항목 6종 (migration 106, 2026-08-20) — 전부 선택 입력. */
  nameEn?: string | null;
  extension?: string | null;
  deskLocation?: string | null;
  bio?: string | null;
  /** 연도 없는 'MM-DD'. */
  birthday?: string | null;
  timezone?: string | null;
  /**
   * 「우리 조직」 (2026-08-28 · 자리 재정의 판 5) — 이 사람이 **누구의 것을 볼 수 있나**.
   *
   * ★★ **이것만 메뉴 권한으로 못 한다** — 팀장은 「그 팀의 리더」라는 개인 지정이라,
   *    메뉴 권한을 부서로 켜면 **팀원 전원**에게 뜬다. 그래서 축이 따로다(조직 지정).
   * ★ 옛 서버에는 없다 → `undefined`. 그때는 「우리 조직」을 안 그린다(있는 척하지 않는다).
   */
  orgScope?: 'ALL' | 'DIVISION' | 'TEAM' | 'NONE';
  leadTeams?: Array<{ id: string; name: string }>;
  leadDivisions?: Array<{ id: string; name: string }>;
  isRepresentative?: boolean;
  /**
   * 「전사 근태를 볼 수 있나」 (2026-08-31) — 근태 셸이 **묻지 않고** 「근태 관리」를 세우는 근거.
   *
   * ★★ 근태 담당자는 역할이 `STAFF` 다(담당자 부여). 그래서 역할로는 직원과 구별할 수 없고,
   *    알아내려 전사 라우트를 찔러 보면 직원마다 403 이 쌓인다 — 그 자국을 #847 이 지웠다.
   * ★ 옛 서버에는 없다 → `undefined`. 그때는 **거짓처럼 다룬다**(모르면 안 보여 주는 쪽).
   */
  canSeeCompanyAttendance?: boolean;
  org?: {
    teamId: string;
    teamName: string;
    divisionId: string | null;
    divisionName: string | null;
    isLeader: boolean;
    joinedAt: string;
  } | null;
}

/**
 * 본인이 자기 프로필에서 고칠 수 있는 것.
 *
 * ★★ **`position`(직위)은 여기 없다** (2026-08-27). 발령·급여·결재 사다리에 얽힌 값이라
 *    담당자와 발령만 바꾼다 — 서버도 본인이 보내면 403(`POSITION_READONLY`)이다.
 *    타입에서 빼 두면 실수로 다시 보낼 수 없다.
 */
/**
 * 본인 프로필 저장 — **본인이 고칠 수 있는 칸만** 있다(2026-09-13 사용자 결정).
 * name · nameEn · birthday · 사진은 인사에서 관리자가 고친다 → 실으면 서버가 403 `PROFILE_FIELD_READONLY`.
 * 직위와 같은 이유로 타입에서도 뺐다 — 다시 싣는 코드는 컴파일에서 막힌다.
 */
export interface PatchProfileInput {
  phone?: string | null;
  avatarUrl?: string | null;
  extension?: string | null;
  deskLocation?: string | null;
  bio?: string | null;
  timezone?: string | null;
}

export interface UserSettingsDto {
  themeColorKey: string;
  darkMode: boolean;
  /** 테마 3-상태(§17.2). 서버가 darkMode 와 함께 반환. */
  themeMode: 'light' | 'dark' | 'auto';
  /** 캘린더 테마 key (admin 등록 테마, 없는/비활성 키면 프론트 'default' fallback). */
  calendarThemeKey: string;
  language: string;
  notifChat: boolean;
  notifMail: boolean;
  notifCalendar: boolean;
  notifTask: boolean;
  /* 테마 커스터마이즈 (migration 106) — 색이 null 이면 고른 테마의 기본색을 쓴다. */
  themePrimary?: string | null;
  themeAccent?: string | null;
  themeDanger?: string | null;
  themeLine?: string | null;
  themeSurface?: string | null;
  buttonRadius?: 'square' | 'round' | 'pill';
  themeTransitionMs?: number;
  /* 영역별 색 (migration 109) — NULL 이면 그 영역은 지금까지의 색을 쓴다. */
  themeBg?: string | null;
  themeRail?: string | null;
  themeMenu?: string | null;
  themeRrail?: string | null;
  themeStatus?: string | null;
  themeTable?: string | null;
  themeTableHead?: string | null;
  themeTableHeadText?: string | null;
  themeIcon?: string | null;
  themeIconHover?: string | null;
  themeTint?: string | null;
  themeText?: string | null;
  themeTextMuted?: string | null;
  /* 등록한 테마 목록 (migration 108) — 구 서버는 이 키를 아예 내려주지 않는다(undefined).
     그래서 "빈 배열(등록한 게 없다)" 과 "서버가 모른다" 를 화면이 구분할 수 있다. */
  themePresets?: SavedTheme[] | null;
  /* 개인 메일 설정 (migration 110) — 구 서버는 이 키를 안 내려준다(undefined).
     화면은 undefined 를 "서버가 모른다" 로 보고 지금까지의 기본값을 쓴다. */
  mailFirstFolder?: 'inbox' | 'star' | 'sent' | 'draft';
  mailPageSize?: number;
  mailComposeMode?: 'HTML' | 'TEXT' | 'MARKDOWN';
  mailQuoteDivider?: boolean;
  mailSimplifySubject?: boolean;
  /* 「환경설정 > 기본환경」 (migration 158) — 위와 같은 규칙: undefined = 서버가 모른다.
     ★ 이 키들을 모르는 서버에 보내면 **400** 이다(PATCH 스키마가 닫혀 있다) —
       화면이 그것을 「저장 못 함」으로 알린다(MailPrefsPanel 이 쓰던 그 처리). */
  mailAddrDisplay?: MailAddrDisplay;
  mailBlockImages?: boolean;
  mailBlockUnsafeTags?: boolean;
  mailShowOrigin?: boolean;
  mailSearchAll?: boolean;
  mailListShowCc?: boolean;
  mailListDensity?: MailListDensity;
  mailRecipientInput?: MailRecipientInput;
  mailSendConfirm?: boolean;
  mailSaveSent?: boolean;
  mailReadReceipt?: boolean;
  mailComposeWindow?: MailComposeWindow;
  mailEncoding?: MailEncoding;
  mailDraftAlert?: boolean;
  mailSenderName?: string | null;
  mailReplyTo?: string | null;
  mailVcardAttach?: boolean;
  mailVcard?: MailVcard | null;
  /* 「환경설정 > 스팸」 (migration 160) — 정책 · 등급 · 처리방법. */
  mailSpamPolicy?: MailSpamPolicy;
  mailSpamLevel?: MailSpamLevel;
  mailSpamAction?: MailSpamAction;
  /* 「환경설정 > 자동분류」 (migration 161) — 규칙 전체를 켜고 끄는 스위치. 기본은 끔. */
  mailFilterEnabled?: boolean;
  /* 「환경설정 > 자동전달」 (migration 162) — 받은 메일을 다른 주소로 보낼지. 기본은 끔. */
  mailForwardEnabled?: boolean;
}

/** 메일주소 표시방식 — 이름만 / 이름 + 메일주소. */
export type MailAddrDisplay = 'NAME' | 'NAME_EMAIL';
/** 목록 밀도 — 좁게(압축) / 넓게(아바타·미리보기). 목록 상단 View 메뉴와 **같은 값**이다. */
export type MailListDensity = 'NARROW' | 'WIDE';
/** 받는사람 추가방식. */
export type MailRecipientInput = 'AUTOCOMPLETE' | 'NAME_ONLY' | 'SEARCH';
/** 메일쓰기 방식 — 화면(일반쓰기) / 팝업쓰기. */
export type MailComposeWindow = 'PAGE' | 'POPUP';
/** 메일 인코딩 — 서버 PATCH 스키마의 enum 과 **같은 순서·같은 값**이어야 한다. */
export type MailEncoding = 'UTF-8' | 'EUC-KR' | 'US-ASCII' | 'ISO-2022-JP' | 'GB2312' | 'BIG5';

/**
 * 스팸차단 정책 다섯 (migration 160).
 * ★ 서버 `mail-spam-policy.ts` 의 목록과 **같은 값**이어야 한다 — 다르면 PATCH 가 400 이다.
 */
export type MailSpamPolicy = 'AUTO' | 'OFF' | 'ALL_RECEIVE' | 'ALL_BLOCK' | 'ALLOW_ONLY';
/** 자동 차단 등급 — 정책이 AUTO 일 때만 쓴다. */
export type MailSpamLevel = 'HIGH' | 'MEDIUM' | 'LOW';
/** 걸린 메일을 어디로 — 스팸메일함 / 휴지통 / 완전삭제. */
export type MailSpamAction = 'SPAM_BOX' | 'TRASH' | 'DELETE';

/**
 * 명함 한 장 (v-card).
 *
 * ★ 뒤 넷(집전화·집주소·메신저·회사홈페이지)은 「항목 추가」로 붙는 칸이다 —
 *   **서버 PATCH 스키마에도 같은 이름이 적혀 있어야** 저장된다(additionalProperties:false).
 */
export interface MailVcard {
  /** 지금은 프로필 사진을 쓴다 — 명함 전용 사진 올리기는 아직 없다. */
  photoUrl?: string | null;
  name?: string;
  org?: string;
  email?: string;
  mobile?: string;
  officeTel?: string;
  officeAddr?: string;
  memo?: string;
  homeTel?: string;
  homeAddr?: string;
  messenger?: string;
  homepage?: string;
}

export type PatchSettingsInput = Partial<UserSettingsDto>;

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

/** 본인 프로필 조회 — GET /api/v1/users/me (이름·직책·전화·avatarUrl 일괄). */
export async function getMyProfile(): Promise<ProfileDto> {
  const { data } = await apiClient.get<{ user: ProfileDto }>('/users/me');
  return data.user;
}

/**
 * 본인 아바타 업로드 — PUT /api/v1/users/me/avatar (raw 이미지 바이너리).
 * 운영 MinIO 가 브라우저 비공개라 presigned 직결 대신 chat-server 가 대리 저장(design §17).
 */
export async function uploadMyAvatar(blob: Blob, contentType: string): Promise<ProfileDto> {
  const { data } = await apiClient.put<{ user: ProfileDto }>('/users/me/avatar', blob, {
    headers: { 'Content-Type': contentType },
  });
  return data.user;
}

/** 본인 아바타 삭제 — DELETE /api/v1/users/me/avatar. */
export async function deleteMyAvatar(): Promise<ProfileDto> {
  const { data } = await apiClient.delete<{ user: ProfileDto }>('/users/me/avatar');
  return data.user;
}

/** 본인 프로필 편집 — backend PATCH /api/v1/users/:id (본인 또는 ADMIN). */
export async function patchMyProfile(myId: string, input: PatchProfileInput): Promise<ProfileDto> {
  const { data } = await apiClient.patch<{ user: ProfileDto }>(`/users/${myId}`, input);
  return data.user;
}

/** 환경설정 조회 — GET /api/v1/users/me/settings (default 자동 upsert). */
export async function getMySettings(): Promise<UserSettingsDto> {
  const { data } = await apiClient.get<{ settings: UserSettingsDto }>('/users/me/settings');
  return data.settings;
}

/** 환경설정 수정 — PATCH /api/v1/users/me/settings (partial). */
export async function patchMySettings(input: PatchSettingsInput): Promise<UserSettingsDto> {
  const { data } = await apiClient.patch<{ settings: UserSettingsDto }>('/users/me/settings', input);
  return data.settings;
}

/**
 * 비밀번호 변경 — POST /api/v1/users/me/password.
 * 정책:
 *   - 401 INVALID_PASSWORD: currentPassword 불일치 또는 SSO 사용자
 *   - 422 PASSWORD_POLICY_VIOLATED: violations 배열 동반
 *   - 422 PASSWORD_REUSED: new === current
 *   - 200 { ok: true }
 */
export async function changePassword(input: ChangePasswordInput): Promise<{ ok: true }> {
  const { data } = await apiClient.post<{ ok: true }>('/users/me/password', input);
  return data;
}

/** 동일 institution 내 사용자 단건 조회 — GET /api/v1/users/:id (이름·아바타 해석용). */
export async function getUser(id: string): Promise<UserSummary> {
  const { data } = await apiClient.get<{ user: UserSummary }>(`/users/${id}`);
  return data.user;
}

interface ListUsersResponse {
  users: UserSummary[];
  nextCursor: string | null;
}

/**
 * F4 (review-findings Loop1) — `signal` 로 inflight 네트워크 abort. RecipientPicker 의 cleanup
 * 이 abortController.abort() 를 호출하면 axios 가 즉시 reject (CanceledError) → backend 가 응답
 * 직렬화·DB scan 을 끝낼 필요 없음. 빠른 query change 시 backend 부하 폭증 차단.
 */
export async function searchUsers(query: string, limit = 10, signal?: AbortSignal): Promise<UserSummary[]> {
  const { data } = await apiClient.get<ListUsersResponse>('/users', {
    params: { search: query, limit },
    signal,
  });
  return Array.isArray(data?.users) ? data.users : [];
}
